/**
 * AK-389 — мини-карта точки осмотра: контур поля + 📍 GPS-точка.
 *
 * Намеренно SVG, а не MapLibre-инстанс в Sheet: (1) дешевле — ноль тайлов/сети,
 * (2) без teardown-граблей MapLibre при размонтировании панели (память проекта:
 * V2 map teardown ронял getLayer на снесённой карте), (3) детерминированно и
 * юнит-тестируемо. Контекст «точка относительно контура» этого достаточно —
 * ровно то, что показывает low-fi.
 *
 * Цвета — только semantic токены через currentColor (никакого hex): контур —
 * `text-primary`, точка — `text-accent-brand` с гало цвета фона.
 */
import {
  geojsonToRings,
  projectMiniMap,
  ringToPath,
  type InspectionLocation,
} from './inspectionLogic';

interface Props {
  point: InspectionLocation;
  geojson?: { type?: string; coordinates?: unknown } | null;
  className?: string;
}

export function InspectionMiniMap({ point, geojson, className }: Props) {
  const rings = geojsonToRings(geojson);
  const proj = projectMiniMap(rings, point);
  if (!proj) return null;

  return (
    <div
      className={
        'relative overflow-hidden rounded-lg border border-border bg-muted/40 ' +
        (className ?? '')
      }
    >
      <svg
        viewBox={`0 0 ${proj.size} ${proj.size}`}
        className="h-full w-full"
        role="img"
        aria-label="Точка осмотра на контуре поля"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Контур поля */}
        <g className="text-primary">
          {proj.ringsXY.map((ring, i) => (
            <path
              key={i}
              d={ringToPath(ring)}
              fill="currentColor"
              fillOpacity={0.12}
              stroke="currentColor"
              strokeWidth={1}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </g>
        {/* GPS-точка: гало цвета фона + янтарная сердцевина */}
        {proj.pointXY && (
          <>
            <circle
              cx={proj.pointXY.x}
              cy={proj.pointXY.y}
              r={4}
              className="text-background"
              fill="currentColor"
            />
            <circle
              cx={proj.pointXY.x}
              cy={proj.pointXY.y}
              r={2.6}
              className="text-accent-brand"
              fill="currentColor"
            />
          </>
        )}
      </svg>
    </div>
  );
}

export default InspectionMiniMap;
